import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemType, Item, ShopMessage, CartItem } from './types';
import { SHOP_ITEMS_PRICE, calculateTotalPrice, getItemPrice } from './shopConfig';
import {
  ShopOverlay,
  ShopPanel,
  ShopTitle,
  ShopCloseButton,
  ShopItemsContainer,
  ShopItemCard,
  ShopItemIcon,
  ShopItemName,
  ShopItemDescription,
  ShopItemPrice,
  QuantitySelector,
  QuantityButton,
  QuantityDisplay,
  CartSummary,
  TotalPrice,
  BuyButton,
  ShopMessageContainer,
  ShopMessageItem,
  RechargeButton,
  DiamondIcon,
  DiamondDisplay,
  ShopHeader
} from './styles';

interface ShopProps {
  isOpen: boolean;
  onClose: () => void;
  items: Item[];
  diamonds: number;
  onPurchase: (purchasedItems: CartItem[]) => void;
  onRecharge: () => void;
}

/**
 * 商店组件
 * 负责展示可购买的道具、管理购物车、处理购买流程
 */
const Shop: React.FC<ShopProps> = ({ 
  isOpen, 
  onClose,
  items, 
  diamonds,
  onPurchase,
  onRecharge
}) => {
  const { t } = useTranslation();
  // 购物车状态，使用Map存储商品类型和数量
  const [cart, setCart] = useState<Map<ItemType, number>>(new Map());
  // 商店消息
  const [messages, setMessages] = useState<ShopMessage[]>([]);
  // 总价
  const [totalPrice, setTotalPrice] = useState(0);
  
  // 当商店打开时，重置购物车
  useEffect(() => {
    if (isOpen) {
      setCart(new Map());
      setTotalPrice(0);
    }
  }, [isOpen]);
  
  // 当购物车变化时，计算总价
  useEffect(() => {
    setTotalPrice(calculateTotalPrice(cart));
  }, [cart]);
  
  // 显示商店消息
  const showMessage = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const newMessage: ShopMessage = {
      id: `shop-msg-${Date.now()}`,
      message,
      type,
      timestamp: Date.now()
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    // 3秒后移除消息
    setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.id !== newMessage.id));
    }, 3000);
  };
  
  // 添加商品到购物车
  const addToCart = (itemType: ItemType) => {
    setCart(prev => {
      const newCart = new Map(prev);
      const currentQuantity = newCart.get(itemType) || 0;
      newCart.set(itemType, currentQuantity + 1);
      return newCart;
    });
  };
  
  // 从购物车移除商品
  const removeFromCart = (itemType: ItemType) => {
    setCart(prev => {
      const newCart = new Map(prev);
      const currentQuantity = newCart.get(itemType) || 0;
      
      if (currentQuantity <= 1) {
        newCart.delete(itemType);
      } else {
        newCart.set(itemType, currentQuantity - 1);
      }
      
      return newCart;
    });
  };
  
  // 处理购买
  const handlePurchase = () => {
    // 检查钻石是否足够
    if (diamonds < totalPrice) {
      showMessage(t('shop.notEnough'), 'error');
      return;
    }
    
    // 准备购买的商品列表
    const purchasedItems: CartItem[] = [];
    cart.forEach((quantity, itemType) => {
      purchasedItems.push({ itemType, quantity });
    });
    
    // 调用父组件的购买方法
    onPurchase(purchasedItems);
    
    // 显示成功消息
    showMessage(t('shop.success'), 'success');
    
    // 清空购物车
    setCart(new Map());
  };
  
  // 计算是否可以购买（总价大于0且钻石足够）
  const canPurchase = totalPrice > 0 && diamonds >= totalPrice;
  
  if (!isOpen) return null;
  
  return (
    <ShopOverlay>
      <ShopPanel>
        <ShopHeader>
          <ShopTitle>{t('shop.title')}</ShopTitle>
          <ShopCloseButton onClick={onClose}>✕</ShopCloseButton>
        </ShopHeader>
        
        <ShopItemsContainer>
          {items.map(item => {
            const price = getItemPrice(item.type);
            const cartQuantity = cart.get(item.type) || 0;
            
            return (
              <ShopItemCard key={item.type}>
                <ShopItemIcon>{item.icon}</ShopItemIcon>
                <ShopItemName>{item.name}</ShopItemName>
                <ShopItemDescription>{item.description}</ShopItemDescription>
                <ShopItemPrice>
                  <DiamondIcon>💎</DiamondIcon> {price}
                </ShopItemPrice>
                
                <QuantitySelector>
                  <QuantityButton 
                    onClick={() => removeFromCart(item.type)}
                    disabled={cartQuantity === 0}
                  >
                    -
                  </QuantityButton>
                  <QuantityDisplay>{cartQuantity}</QuantityDisplay>
                  <QuantityButton 
                    $increase 
                    onClick={() => addToCart(item.type)}
                  >
                    +
                  </QuantityButton>
                </QuantitySelector>
              </ShopItemCard>
            );
          })}
        </ShopItemsContainer>
        
        <CartSummary>
          <TotalPrice>
            <span>{t('shop.total')}：</span>
            <span>
              <DiamondIcon>💎</DiamondIcon> {totalPrice}
            </span>
          </TotalPrice>
          <DiamondDisplay>
           {t('shop.myDiamonds')}<DiamondIcon>💎：</DiamondIcon>
           <span>{diamonds}</span>
          </DiamondDisplay>
          
          <BuyButton
            $enabled={canPurchase}
            onClick={handlePurchase}
            disabled={!canPurchase}
          >
            {t('shop.buy')}
          </BuyButton>
          
          <RechargeButton onClick={onRecharge}>
            {t('shop.recharge')}
          </RechargeButton>
        </CartSummary>
        
        <ShopMessageContainer>
          {messages.map(message => (
            <ShopMessageItem 
              key={message.id} 
              $type={message.type}
            >
              {message.message}
            </ShopMessageItem>
          ))}
        </ShopMessageContainer>
      </ShopPanel>
    </ShopOverlay>
  );
};

export default Shop; 